/**
 * Client-side barcode decoder for prescription labels.
 *
 * TWO-LAYER pipeline:
 *   1. Browser `BarcodeDetector` API (Chrome 83+, Edge 83+, Safari 17+).
 *      It's fast, native, and supports the same formats we care about.
 *   2. `@zxing/library` fallback for browsers without `BarcodeDetector`
 *      (notably Firefox as of 2026 still has it behind a flag) AND as a
 *      retry when BarcodeDetector returns nothing (it gives up faster
 *      than ZXing's TRY_HARDER pass).
 *
 * Returns `{ text, format }` on success, `null` on failure. Never throws.
 */
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";

// ---------------------------------------------------------------------------
// Layer 1 — BarcodeDetector
// ---------------------------------------------------------------------------

const NATIVE_FORMATS = [
  "code_128", "code_39", "data_matrix", "qr_code",
  "ean_13", "ean_8", "upc_a", "upc_e", "pdf417", "aztec",
];

// Map BarcodeDetector format strings → our canonical UPPER_SNAKE labels
// so the rest of the app sees the same symbology name regardless of which
// layer decoded the barcode.
const NATIVE_TO_CANONICAL = {
  "code_128": "CODE_128", "code_39": "CODE_39", "data_matrix": "DATA_MATRIX",
  "qr_code": "QR_CODE", "ean_13": "EAN_13", "ean_8": "EAN_8",
  "upc_a": "UPC_A", "upc_e": "UPC_E", "pdf417": "PDF_417", "aztec": "AZTEC",
};

export function isNativeBarcodeDetectorAvailable() {
  return typeof window !== "undefined" && typeof window.BarcodeDetector !== "undefined";
}

let _nativeDetector = null;
async function getNativeDetector() {
  if (!isNativeBarcodeDetectorAvailable()) return null;
  if (_nativeDetector) return _nativeDetector;
  try {
    // Some browsers gate the format list via getSupportedFormats(). Best-effort.
    let formats = NATIVE_FORMATS;
    if (typeof window.BarcodeDetector.getSupportedFormats === "function") {
      const supported = await window.BarcodeDetector.getSupportedFormats();
      formats = NATIVE_FORMATS.filter((f) => supported.includes(f));
      if (formats.length === 0) formats = NATIVE_FORMATS;
    }
    _nativeDetector = new window.BarcodeDetector({ formats });
    return _nativeDetector;
  } catch {
    return null;
  }
}

async function decodeWithNative(file) {
  const detector = await getNativeDetector();
  if (!detector) return null;
  try {
    // BarcodeDetector accepts ImageBitmapSource — Blob / File works directly.
    const bitmap = await createImageBitmap(file);
    const results = await detector.detect(bitmap);
    bitmap.close?.();
    if (!results || results.length === 0) return null;
    const r = results[0];
    const text = r?.rawValue || "";
    if (!text) return null;
    const fmt = NATIVE_TO_CANONICAL[r?.format] || (r?.format ? r.format.toUpperCase() : null);
    return { text, format: fmt };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Layer 2 — ZXing fallback (unchanged from previous build)
// ---------------------------------------------------------------------------

let _reader = null;
function getReader() {
  if (_reader) return _reader;
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.QR_CODE,
    BarcodeFormat.EAN_13,
    BarcodeFormat.UPC_A,
    BarcodeFormat.PDF_417,
  ]);
  hints.set(DecodeHintType.TRY_HARDER, true);
  _reader = new BrowserMultiFormatReader(hints);
  return _reader;
}

async function decodeWithZxing(file) {
  const reader = getReader();
  const url = URL.createObjectURL(file);
  try {
    const result = await reader.decodeFromImageUrl(url);
    const text = result?.getText?.() || null;
    if (!text) return null;
    let format = null;
    try {
      const fmt = result.getBarcodeFormat?.();
      if (fmt != null) {
        const found = Object.keys(BarcodeFormat).find((k) => BarcodeFormat[k] === fmt);
        if (found) format = found;
      }
    } catch { /* format is optional */ }
    return { text, format };
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ---------------------------------------------------------------------------
// Public API — try native first, fall back to ZXing
// ---------------------------------------------------------------------------

/**
 * Decode a barcode from a File / Blob. Returns the decoded text, or null.
 * Backwards-compatible string-only return shape kept for legacy callers.
 */
export async function decodeBarcodeFromFile(file) {
  if (!file) return null;
  const result = await decodeBarcodeDetailedFromFile(file);
  return result?.text || null;
}

/**
 * Decode a barcode and return `{ text, format, source }`. `source` tells
 * the caller which layer decoded it ("native" | "zxing"), useful for the
 * Rx Scan Review UI ("Barcode decoded · native" vs "Barcode decoded · ZXing").
 */
export async function decodeBarcodeDetailedFromFile(file) {
  if (!file) return null;
  const native = await decodeWithNative(file);
  if (native?.text) return { ...native, source: "native" };
  const zx = await decodeWithZxing(file);
  if (zx?.text) return { ...zx, source: "zxing" };
  return null;
}

/**
 * Classify a decoded barcode value into a likely TYPE so the UI can show
 * the right affordances:
 *   - "ndc"       : looks like an NDC (10–12 digits, possibly hyphenated)
 *   - "rx-number" : alphanumeric, 4–32 chars, no spaces — matches an Rx#
 *   - "gs1"       : starts with GS1 application identifier (e.g. "01" prefix on a 14-digit GTIN)
 *   - "internal"  : everything else — stored as opaque internal pharmacy code
 *
 * The classifier is intentionally generous: when a value could be both
 * "ndc" and "rx-number" (e.g. a 10-digit numeric), we return "ndc" since
 * the FDA lookup is the more valuable action for the user.
 */
export function classifyBarcode(rawValue) {
  if (!rawValue) return "internal";
  const s = String(rawValue).trim();
  const digitsOnly = s.replace(/\D/g, "");
  // GS1 — usually a 14-digit GTIN preceded by "01" Application Identifier
  if (/^01\d{14}/.test(digitsOnly) || /^01\d{14}/.test(s)) return "gs1";
  // NDC — 10–12 contiguous digits, optionally hyphenated as 5-4-2 / 5-3-2 / 4-4-2.
  // Allow segments up to 5 digits (labeler), 4 digits (product), 2 digits (package).
  if (/^\d{1,5}-?\d{1,4}-?\d{1,2}$/.test(s) && digitsOnly.length >= 8 && digitsOnly.length <= 12) {
    return "ndc";
  }
  if (/^\d{8,12}$/.test(s)) return "ndc";
  // Rx number — alphanumeric, 4–32 chars, no spaces
  if (/^[A-Za-z0-9\-_/.]{4,32}$/.test(s)) return "rx-number";
  return "internal";
}
